import { useState } from "react";
import { API_URL } from "../../context/AuthContext";
import toSlug from "./toSlug";
import {
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";

interface NewProductDialogProps {
  open: boolean;
  onClose: () => void;
  token: string | null;
  onCreated: () => void;
  categories: Array<{ id: number; name: string }>;
}

export default function NewProductDialog({ open, onClose, token, onCreated, categories }: NewProductDialogProps) {
  const [slug, setSlug] = useState("");
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [description, setDescription] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleTitleChange = (val: string) => {
    setTitle(val);
    setSlug(toSlug(val));
  };

  const handleClose = () => {
    setSlug("");
    setTitle("");
    setSubtitle("");
    setDescription("");
    setCategoryId("");
    setError("");
    onClose();
  };

  const handleCreate = async () => {
    setError("");
    if (!slug || !title) {
      setError("Tytuł jest wymagany");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/products`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ slug, title, subtitle, description, category_id: categoryId || null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      handleClose();
      onCreated();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} PaperProps={{ sx: { backgroundColor: "#2a2a2a", color: "#f0f0f0" } }}>
      <DialogTitle>Nowy produkt</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "16px !important" }}>
        {error && <Alert severity="error">{error}</Alert>}
        <FormControl size="small" fullWidth>
          <InputLabel sx={{ color: "#aaa" }}>Kategoria</InputLabel>
          <Select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} label="Kategoria" sx={{ color: "#f0f0f0" }}>
            <MenuItem value="">
              <em>— wybierz —</em>
            </MenuItem>
            {categories.map((c) => (
              <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField size="small" label="Tytuł" value={title} onChange={(e) => handleTitleChange(e.target.value)} InputProps={{ sx: { color: "#f0f0f0" } }} InputLabelProps={{ sx: { color: "#aaa" } }} fullWidth />
        {slug && <Typography variant="caption" sx={{ color: "#888", mt: -1 }}>Adres: /oferta/{slug}</Typography>}
        <TextField size="small" label="Podtytuł" value={subtitle} onChange={(e) => setSubtitle(e.target.value)} multiline rows={2} InputProps={{ sx: { color: "#f0f0f0" } }} InputLabelProps={{ sx: { color: "#aaa" } }} fullWidth />
        <TextField size="small" label="Opis" value={description} onChange={(e) => setDescription(e.target.value)} multiline rows={4} InputProps={{ sx: { color: "#f0f0f0" } }} InputLabelProps={{ sx: { color: "#aaa" } }} fullWidth />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} sx={{ color: "#aaa" }}>Anuluj</Button>
        <Button onClick={handleCreate} disabled={loading} variant="contained" sx={{ backgroundColor: "#1976d2", "&:hover": { backgroundColor: "#125ea8" } }}>
          {loading ? "Tworzenie..." : "Utwórz produkt"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
